/**
 * PATH: packages/job-hub/src/jobs-list/jobsHubDerive.ts
 * FILE_ID: JOBHUB-JOBSLIST-HUBDERIVE-001
 * PURPOSE: Pure derivations for the Jobs Hub lane page — status buckets,
 *          badge colour map, labels, sort comparator. Moved VERBATIM from
 *          apps/newmark JobsHubPage.tsx (W5b lift); the comments encode
 *          paid-for operator decisions, keep them.
 * WHY: frame architecture — the page frame keeps only hooks/guards/JSX;
 *      every derivation lives here where it is testable in isolation.
 * RELATED:
 * - packages/job-hub/src/jobs-list/JobsHubListPage.tsx (frame consumer)
 * - packages/job-hub/src/jobs/jobRowMenu.ts (JOB_STATUS_OPTIONS source)
 */
import { allTokensMatchFields, tokenizeSearchQuery } from '@quotech/ui'
import { JOB_STATUS_OPTIONS } from '../jobs/jobRowMenu'
import type { JobListRow } from './types'
import { getJobValue } from './servedJobMetrics'

// Status the person reads on the row. The filter offers these same four
// words and nothing else. Ongoing / not started / dormant stay as the small
// line under the pill; they are progress, not a second status list.
//
// A financially complete row reads "Completed" even when lifecycle is still
// wip (Simpro can lag). The filter uses that same displayed word.

export type StatusBucketKey = 'tbc' | 'wip' | 'done' | 'bin'

const LIFECYCLE_STATUS_KEYS: readonly StatusBucketKey[] = ['tbc', 'wip', 'done', 'bin']
const LEGACY_STATUS_BUCKETS = new Set(['active_works', 'ongoing', 'not_started', 'dormant'])
const LEGACY_DEFAULT_BUCKETS = ['active_works', 'ongoing', 'not_started']

/**
 * The word the status pill prints, as a stored key.
 * Inputs: lifecycle `status` plus optional `combined_status`.
 * Output: tbc | wip | done | bin, or the raw status when it is none of those.
 */
export function displayedJobStatusKey(job: {
  status?: string | null
  combined_status?: string | null
}): string {
  const qualifier = String(job.combined_status ?? '').toLowerCase()
  if (qualifier === 'completed') return 'done'
  const status = String(job.status ?? '').toLowerCase()
  if (status === 'wip' || status === 'active' || status === 'in_progress' || status === 'deposit') {
    return 'wip'
  }
  if (status === 'tbc') return 'tbc'
  if (status === 'done' || status === 'completed') return 'done'
  if (status === 'bin' || status === 'archived' || status === 'cancelled') return 'bin'
  return status
}

/**
 * Saved boards still store the old progress buckets. The shipped default
 * (Active Works + Ongoing + Not Started) becomes Active Works only, because
 * Not Started was the Inactive pill and people did not choose it.
 * An empty list stays empty: that is "All statuses".
 */
export function migrateJobsStatusSelection(
  values: readonly string[] | null | undefined,
): StatusBucketKey[] {
  if (!values) return ['wip']
  const cleaned = values.map((value) => value.trim()).filter(Boolean)
  if (cleaned.length === 0) return []
  if (cleaned.every((value) => (LIFECYCLE_STATUS_KEYS as readonly string[]).includes(value))) {
    return [...new Set(cleaned)] as StatusBucketKey[]
  }
  const legacy = cleaned.filter((value) => LEGACY_STATUS_BUCKETS.has(value))
  if (legacy.length === 0) return ['wip']
  const chosen = new Set(legacy)
  const isLegacyDefault = LEGACY_DEFAULT_BUCKETS.length === chosen.size
    && LEGACY_DEFAULT_BUCKETS.every((key) => chosen.has(key))
  if (isLegacyDefault) return ['wip']
  const next = new Set<StatusBucketKey>()
  if (chosen.has('active_works') || chosen.has('ongoing') || chosen.has('dormant')) next.add('wip')
  if (chosen.has('not_started')) next.add('tbc')
  return next.size > 0 ? [...next] : ['wip']
}

export function shouldTrustHubServerSearch(
  search: string,
  debouncedSearch: string,
): boolean {
  return Boolean(debouncedSearch.trim()) && search.trim() === debouncedSearch.trim()
}

export const STATUS_BUCKETS: { key: StatusBucketKey; label: string; predicate: (j: JobListRow) => boolean }[] = [
  { key: 'tbc', label: 'Inactive', predicate: (job) => displayedJobStatusKey(job) === 'tbc' },
  { key: 'wip', label: 'Active Works', predicate: (job) => displayedJobStatusKey(job) === 'wip' },
  { key: 'done', label: 'Completed', predicate: (job) => displayedJobStatusKey(job) === 'done' },
  { key: 'bin', label: 'Archive', predicate: (job) => displayedJobStatusKey(job) === 'bin' },
]

// Status pill / EditableSelect badge colour mapping. The `EditableSelect`
// `variant='badge'` reads the option.color for the trigger styling, so
// these classes must include both background AND text colour to render.
// People table parity (TeamsHub StatusPill): Inactive is muted, not blue.
// Keep 100/700 (or muted) weights so 12px pills clear axe contrast.
export const STATUS_BADGE_CLASS: Record<string, string> = {
  tbc: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  wip: 'bg-green-100 text-green-700 border-green-200',
  done: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  bin: 'bg-red-100 text-red-700 border-red-200',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_progress: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  deposit: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-slate-100 text-slate-700 border-slate-200',
  overdue: 'bg-rose-50 text-rose-700 border-rose-200',
  cancelled: 'bg-zinc-100 text-zinc-500 border-zinc-200 line-through',
  archived: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  prospect: 'bg-violet-50 text-violet-700 border-violet-200',
}

export const STATUS_BADGE_OPTIONS = JOB_STATUS_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
  color: STATUS_BADGE_CLASS[o.value] ?? 'bg-slate-50 text-slate-700 border-slate-200',
}))

// Card palette stays stage/health-oriented, but without blue-* accents so the
// board matches People directory token language.
export const CARD_STATUS_BADGE_CLASS: Record<string, string> = {
  wip: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400',
  done: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-700/50 dark:text-slate-300',
  tbc: 'bg-zinc-100 text-zinc-700 ring-zinc-200 dark:bg-zinc-800/50 dark:text-zinc-300',
  bin: 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700/50 dark:text-slate-400',
}

export const CARD_STATUS_BADGE_OPTIONS = JOB_STATUS_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
  color: CARD_STATUS_BADGE_CLASS[o.value] ?? 'bg-slate-100 text-slate-700 ring-slate-200',
}))

export function statusLabel(status: string): string {
  // Case-folded like the donor's `key = stat.toLowerCase()`. Both backends are
  // in play here (sam-api emits `WIP`, the Django list emits `wip`), so an
  // exact match would silently fall through to the capitalize path and print
  // "Wip" on one tenant and "Active Works" on the other.
  const key = status.toLowerCase()
  const found = JOB_STATUS_OPTIONS.find((o) => o.value === key)
  if (found) return found.label
  return status
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')
}

// Progress words that restate the status pill. "ongoing" is Active Works,
// "completed" is Completed, "not started" is Inactive. A different pair,
// such as Archive plus "not started", still says something new.
const REPEATED_PROGRESS: Record<string, readonly string[]> = {
  ongoing: ['wip', 'active', 'in_progress', 'deposit'],
  completed: ['done', 'completed'],
  'not started': ['tbc'],
}

/**
 * Whether a combined-status chip would repeat the status pill.
 * Inputs: lifecycle status and the optional combined_status wire value.
 * Output: true when the card should omit the second chip.
 */
export function combinedStatusRepeatsLabel(
  status: string | null | undefined,
  combinedStatus: string | null | undefined,
): boolean {
  const qualifier = String(combinedStatus ?? '').replace(/_/g, ' ').trim().toLowerCase()
  if (!qualifier) return true
  if (qualifier === statusLabel(String(status ?? '')).trim().toLowerCase()) return true
  const statusKey = String(status ?? '').trim().toLowerCase()
  return (REPEATED_PROGRESS[qualifier] ?? []).includes(statusKey)
}

// Sortable column keys — kept tight for v1. The spreadsheet at
// /jobs/tracker handles the wider/inline-editable matrix.
export type HubSortKey =
  | 'job_number'
  | 'client_name'
  | 'site_address'
  | 'value'
  | 'wip'
  | 'total_invoiced'
  | 'percent_complete'
  | 'status'
  | 'current_stage'
  | 'start_date'
  | 'end_date'
export type HubSortDir = 'asc' | 'desc'

/** Column header labels for the "Sorted by …" chip — must match JobsHubListTable. */
export const HUB_SORT_LABELS: Record<HubSortKey, string> = {
  job_number: 'Job #',
  client_name: 'Client Name',
  site_address: 'Job Address',
  value: 'Contract Value',
  wip: 'WIP',
  total_invoiced: 'Invoiced',
  percent_complete: '% Complete',
  status: 'Status',
  current_stage: 'Stage',
  start_date: 'Start',
  end_date: 'End',
}

/**
 * The ordering the board opens on. Named because the active-sort chip has to
 * know whether the current ordering is the default: the donor makes the
 * default chip static and only offers a reset once the operator has moved off
 * it (ActiveFiltersBar.jsx, `isOnlyDefaultWip`).
 */
export const DEFAULT_HUB_SORT: { key: HubSortKey; dir: HubSortDir } = {
  key: 'wip',
  dir: 'desc',
}

export function hubSortLabel(key: HubSortKey, dir: HubSortDir): string {
  return `${HUB_SORT_LABELS[key]} (${dir})`
}

export function compareJobs(a: JobListRow, b: JobListRow, key: HubSortKey, dir: HubSortDir): number {
  let va: string | number = ''
  let vb: string | number = ''
  switch (key) {
    case 'job_number': va = a.job_number; vb = b.job_number; break
    case 'client_name': va = (a.client_name ?? '').toLowerCase(); vb = (b.client_name ?? '').toLowerCase(); break
    case 'site_address': va = (a.site_address ?? '').toLowerCase(); vb = (b.site_address ?? '').toLowerCase(); break
    case 'value': va = getJobValue(a); vb = getJobValue(b); break
    case 'wip': va = a.wip_earned ?? a.wip_balance ?? 0; vb = b.wip_earned ?? b.wip_balance ?? 0; break
    case 'total_invoiced': va = parseFloat(a.total_invoiced) || 0; vb = parseFloat(b.total_invoiced) || 0; break
    case 'percent_complete': va = a.percent_complete ?? 0; vb = b.percent_complete ?? 0; break
    case 'status': va = statusLabel(displayedJobStatusKey(a)); vb = statusLabel(displayedJobStatusKey(b)); break
    case 'current_stage': va = (a.current_stage ?? '').toLowerCase(); vb = (b.current_stage ?? '').toLowerCase(); break
    case 'start_date': va = a.start_date ?? ''; vb = b.start_date ?? ''; break
    case 'end_date': va = a.end_date ?? ''; vb = b.end_date ?? ''; break
  }
  if (va < vb) return dir === 'asc' ? -1 : 1
  if (va > vb) return dir === 'asc' ? 1 : -1
  return 0
}

export function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  const dt = new Date(d)
  if (isNaN(dt.getTime())) return '—'
  return dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' })
}

export type FilterHubJobsOptions = {
  /**
   * When true, `jobs` already came from GET ?search=… (server applied
   * apply_v2_search). Skip the client field re-match so Network hits (PM
   * name, invoice #, etc.) are not wiped to an empty table. Status buckets
   * remain an explicit client-side filter in every search state.
   */
  trustServerSearch?: boolean
}

/**
 * The Jobs Hub filter pipeline (buckets → manager → search) — verbatim
 * from the JobsHubPage filteredJobs useMemo, extracted as a pure I→O
 * function so it is testable without a render.
 */
export function filterHubJobs(
  jobs: JobListRow[],
  statusBuckets: StatusBucketKey[],
  managerFilter: string,
  search: string,
  options?: FilterHubJobsOptions,
): JobListRow[] {
  let rows = jobs
  const trustServerSearch = Boolean(options?.trustServerSearch)

  // Status chips are a deliberate UI filter, including on server search
  // results. The V2 API has no canonical bucket projection to send over the
  // wire, so filtering them locally preserves the operator's selected state.
  if (statusBuckets.length > 0) {
    rows = rows.filter((j) => statusBuckets.some((b) => STATUS_BUCKETS.find((x) => x.key === b)?.predicate(j)))
  }
  if (managerFilter) {
    rows = rows.filter((j) => j.project_manager === managerFilter)
  }
  // W4a — canonical AND-of-tokens matching (shared with the Cmd+K
  // palette). Field selection is unchanged. Skip when server already
  // searched — client fields are a subset of apply_v2_search.
  const tokens = tokenizeSearchQuery(search)
  if (tokens.length > 0 && !trustServerSearch) {
    rows = rows.filter((j) =>
      allTokensMatchFields([j.job_number, j.client_name, j.site_address, j.scope], tokens),
    )
  }
  return rows
}
